import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import moment from 'moment';
import ReactECharts from 'echarts-for-react';
import toastr from 'toastr';
import {
  Card, CardBody, CardHeader,
} from 'reactstrap';

import { get } from '../../helpers/api_helper';

function formatMonthStartLabel(dateString) {
  const parsed = moment(dateString, 'YYYY-MM-DD');
  return parsed.date() === 1 ? parsed.format('MMM') : '';
}

function buildIsuTrendLine(days) {
  const points = days
    .map((day, index) => {
      if (day?.trend_thirty_rate === null || day?.trend_thirty_rate === undefined) {
        return null;
      }

      return {
        x: index,
        y: Number(day.trend_thirty_rate),
      };
    })
    .filter(Boolean);

  if (points.length < 2) {
    return days.map(() => null);
  }

  const minObservedY = Math.min(...points.map((point) => point.y));
  const maxObservedY = Math.max(...points.map((point) => point.y));

  const count = points.length;
  const sumX = points.reduce((sum, point) => sum + point.x, 0);
  const sumY = points.reduce((sum, point) => sum + point.y, 0);
  const sumXY = points.reduce((sum, point) => sum + (point.x * point.y), 0);
  const sumXX = points.reduce((sum, point) => sum + (point.x * point.x), 0);
  const denominator = (count * sumXX) - (sumX * sumX);

  if (denominator === 0) {
    const flatValue = sumY / count;
    return days.map((day) => (
      day?.trend_thirty_rate === null || day?.trend_thirty_rate === undefined ? null : flatValue
    ));
  }

  const slope = ((count * sumXY) - (sumX * sumY)) / denominator;
  const intercept = (sumY - (slope * sumX)) / count;

  return days.map((day, index) => {
    if (day?.trend_thirty_rate === null || day?.trend_thirty_rate === undefined) {
      return null;
    }

    const projected = intercept + (slope * index);
    return Math.min(maxObservedY, Math.max(minObservedY, projected));
  });
}

function summarizeTrend(trendSeries) {
  const values = trendSeries.filter((value) => value !== null && value !== undefined);

  if (values.length < 2) {
    return {
      direction: 'flat',
      delta: null,
    };
  }

  const first = values[0];
  const last = values[values.length - 1];
  const delta = Number((last - first).toFixed(1));
  const changePercent = first > 0 ? Number((((last - first) / first) * 100).toFixed(0)) : null;

  if (Math.abs(delta) < 0.3) {
    return {
      direction: 'flat',
      delta,
      changePercent,
    };
  }

  return {
    direction: delta > 0 ? 'up' : 'down',
    delta,
    changePercent,
  };
}

function UsageHistoryChart(props) {
  const { t, item } = props;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await get(`/api/items/reports/${item.id}/usage-history`);
        setData(response);
      } catch (error) {
        toastr.error(t('Error fetching usage history'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [item.id, t]);

  const days = Array.isArray(data?.days) ? data.days : [];
  const labels = days.map((day) => day.date);
  const shipped = days.map((day) => Number(day.shipped_qty) || 0);
  const isuTrend = buildIsuTrendLine(days);
  const stockPeriods = Array.isArray(data?.stock_periods) ? data.stock_periods : [];
  const latestUsage = data?.latest_usage || null;
  const latestQualifiedUsage = data?.latest_qualified_usage || null;
  const latestThirtyRate = latestQualifiedUsage
    && latestQualifiedUsage.thirty_rate !== null
    && latestQualifiedUsage.thirty_rate !== undefined
    ? Number(latestQualifiedUsage.thirty_rate).toFixed(1)
    : '--';
  const trendSummary = summarizeTrend(isuTrend);
  let trendLabel = t('Flat');
  let trendColor = '#f1b44c';

  if (trendSummary.direction === 'up') {
    trendLabel = t('Up');
    trendColor = '#34c38f';
  } else if (trendSummary.direction === 'down') {
    trendLabel = t('Down');
    trendColor = '#f46a6a';
  }

  const option = {
    backgroundColor: 'transparent',
    animation: false,
    grid: {
      top: 38,
      left: 50,
      right: 52,
      bottom: 52,
    },
    legend: {
      top: 0,
      textStyle: {
        color: '#bfc8d6',
      },
      data: [
        t('Shipped'),
        t('ISU Trend'),
      ],
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(21, 25, 34, 0.96)',
      borderColor: '#2a3042',
      textStyle: {
        color: '#eff2f7',
      },
      formatter: (params) => {
        const day = days[params[0]?.dataIndex];
        const shippedRow = params.find((entry) => entry.seriesName === t('Shipped'));
        const trendRow = params.find((entry) => entry.seriesName === t('ISU Trend'));

        return [
          `<strong>${moment(day.date, 'YYYY-MM-DD').format('ddd, MMM D YYYY')}</strong>`,
          `${t('Shipped')}: ${shippedRow ? shippedRow.value : 0}`,
          `${t('In Stock')}: ${day && day.in_stock ? t('Yes') : t('No')}`,
          `${t('ISU/30 Day')}: ${day && day.thirty_rate !== null && day.thirty_rate !== undefined ? Number(day.thirty_rate).toFixed(1) : '--'}`,
          `${t('Trend')}: ${trendRow && trendRow.value !== null && trendRow.value !== undefined ? Number(trendRow.value).toFixed(1) : '--'}`,
        ].join('<br/>');
      },
    },
    xAxis: {
      type: 'category',
      data: labels,
      boundaryGap: true,
      axisLine: {
        lineStyle: {
          color: '#495057',
        },
      },
      axisLabel: {
        color: '#adb5bd',
        interval: 0,
        formatter: (value) => formatMonthStartLabel(value),
      },
      axisTick: {
        show: true,
        interval: 0,
        alignWithLabel: true,
        lineStyle: {
          color: '#495057',
        },
      },
    },
    yAxis: [
      {
        type: 'value',
        name: t('Shipped'),
        minInterval: 1,
        axisLine: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: 'rgba(255, 255, 255, 0.05)',
          },
        },
        axisLabel: {
          color: '#adb5bd',
        },
        nameTextStyle: {
          color: '#adb5bd',
        },
      },
      {
        type: 'value',
        name: t('ISU/30 Day'),
        position: 'right',
        axisLine: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLabel: {
          color: '#adb5bd',
        },
        nameTextStyle: {
          color: '#adb5bd',
        },
      },
    ],
    series: [
      {
        name: t('Shipped'),
        type: 'bar',
        yAxisIndex: 0,
        data: shipped,
        barWidth: 2,
        itemStyle: {
          color: '#5fa8ff',
          opacity: 0.9,
        },
        emphasis: {
          itemStyle: {
            color: '#82beff',
          },
        },
        markArea: {
          silent: true,
          itemStyle: {
            color: 'rgba(52, 195, 143, 0.11)',
          },
          data: stockPeriods.map((period) => ([
            { xAxis: period.start_date },
            { xAxis: period.end_date },
          ])),
        },
      },
      {
        name: t('ISU Trend'),
        type: 'line',
        yAxisIndex: 1,
        data: isuTrend,
        smooth: true,
        showSymbol: false,
        connectNulls: true,
        lineStyle: {
          color: '#f1b44c',
          width: 3,
          type: 'solid',
        },
        itemStyle: {
          color: '#f1b44c',
        },
        emphasis: {
          focus: 'series',
        },
      },
    ],
  };

  return (
    <Card>
      <CardHeader className="text-uppercase bg-transparent border-bottom">
        {t('1 Year Usage History')}
      </CardHeader>
      <CardBody>
        {latestUsage && (
          <div className="mb-3 d-flex flex-wrap gap-4">
            <div>
              <div className="small text-uppercase" style={{ color: 'var(--bs-body-color)', opacity: 0.72 }}>{t('Average')}</div>
              <div className="fs-5">
                {data.average_thirty_rate !== null ? Number(data.average_thirty_rate).toFixed(1) : '--'}
              </div>
            </div>
            <div>
              <div className="small text-uppercase" style={{ color: 'var(--bs-body-color)', opacity: 0.72 }}>{t('Latest 30 Rate')}</div>
              <div className="fs-5">
                {latestThirtyRate}
              </div>
            </div>
            <div>
              <div className="small text-uppercase" style={{ color: 'var(--bs-body-color)', opacity: 0.72 }}>{t('Trend')}</div>
              <div className="fs-5" style={{ color: trendColor }}>
                {trendLabel}
                {trendSummary.changePercent !== null && (
                  <span className="ms-2 text-body">
                    {trendSummary.changePercent > 0 ? '+' : ''}
                    {trendSummary.changePercent}
                    %
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="small text-uppercase" style={{ color: 'var(--bs-body-color)', opacity: 0.72 }}>
                {latestUsage.out_at === null
                  ? t('Stock Since')
                  : t('OOS Since')}
              </div>
              <div className="fs-6">
                {moment(latestUsage.out_at || latestUsage.in_at).fromNow()}
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-5">
            <span className="mdi mdi-loading mdi-spin fs-2" />
          </div>
        )}

        {!loading && days.length > 0 && (
          <ReactECharts option={option} style={{ height: '320px', width: '100%' }} notMerge lazyUpdate />
        )}

        {!loading && days.length === 0 && (
          <div className="text-muted text-center py-5">{t('No usage history found')}</div>
        )}
      </CardBody>
    </Card>
  );
}

UsageHistoryChart.propTypes = {
  t: PropTypes.any,
  item: PropTypes.object.isRequired,
};

UsageHistoryChart.defaultProps = {
  t: undefined,
};

export default connect(() => ({

}), {})(withTranslation()(UsageHistoryChart));
